import { useCallback } from 'react'
import type { CalendarEvent } from '@highlight/common'
import { useSetSidepanelState } from '@renderer/overlay/atoms'
import { useViewActions, type ViewOpenTarget } from '../../../view-atoms'
import { useSetCalendarEventBriefGenerating } from '@renderer/hooks/use-calendar-events'
import { useTriggerBriefGeneration } from './use-trigger-brief-generation'
import { useMeetingBriefGenerationEnabled } from './use-meeting-brief-generation-enabled'
import { useMeetingHoursExhausted } from './use-meeting-hours-exhausted'
import { MEETING_VIEW_ID } from '../meeting-view-id'
import { AmplitudeRenderer } from '@renderer/utils/amplitude.renderer'

interface OpenBriefParams extends ViewOpenTarget {
  calendarEventId: string
  audioNoteId?: string
  isActive?: boolean
  title?: string
  sourceLabel?: string
  /** Calendar event metadata used to render the same icon in the view header that the cell shows. */
  meeting?: CalendarEvent
}

export function useOpenBrief() {
  const { openView } = useViewActions()
  const setSidepanelState = useSetSidepanelState()
  const setBriefGenerating = useSetCalendarEventBriefGenerating()
  const triggerBrief = useTriggerBriefGeneration()
  const briefGenerationEnabled = useMeetingBriefGenerationEnabled()
  const outOfHours = useMeetingHoursExhausted()
  return useCallback(
    ({
      calendarEventId,
      audioNoteId,
      isActive,
      title,
      meeting,
      sourceLabel = 'Home',
      pointerEvent,
      popout,
    }: OpenBriefParams) => {
      setSidepanelState('open')

      // Opening the View is the intent signal for meetings outside the
      // auto-trigger window. Skip if a brief is ready, already generating, or
      // the meeting has ended. Out of meeting hours the backend refuses the run
      // anyway; skipping here keeps the View from flashing a "preparing" state
      // that resolves to a 403.
      if (!meeting?.brief && briefGenerationEnabled && !outOfHours) {
        const alreadyInFlight = !!meeting?.briefGenerating
        const meetingEnded = meeting?.end ? new Date(meeting.end).getTime() < Date.now() : false
        if (!alreadyInFlight && !meetingEnded) {
          AmplitudeRenderer.trackEvent('Meeting Brief', {
            action: 'generate',
            source: 'open-brief',
            meeting_id: calendarEventId,
          })
          setBriefGenerating(calendarEventId, true)
          triggerBrief.mutate(calendarEventId, { onError: () => setBriefGenerating(calendarEventId, false) })
        }
      }

      openView(
        MEETING_VIEW_ID,
        isActive
          ? { mode: 'live', initialTab: 'brief', calendarEventId, meeting }
          : { mode: 'note', audioNoteId, calendarEventId, title, initialTab: 'brief', meeting },
        { sourceLabel, pointerEvent, popout },
      )
    },
    [openView, setSidepanelState, setBriefGenerating, triggerBrief, briefGenerationEnabled, outOfHours],
  )
}
